import { useEffect, useMemo, useState } from 'react'
import { useGetMediatorAccessTokenFn, useGetStreamingUrlFn, useRecordingSegments, useRecordingUrlFn } from '../../../../api/apiQueries'
import { Activity, ProcessedActivity, RecordingType } from '../../../../api/models'
import { assembleAndValidateRecordingsFromSegments, createObjectUrlsForLiveStreamCaptures, ValidatedRecording } from '../ertUtils'
import { processRRWebEventsForErt } from '../ErtPreprocessor'
import { MediatorFns } from '../../../../api'
import { ProcessedRRWebEvents } from '../rrweb/types'
import { processActivities } from '../activityUtils'
import { LoggerName, namedLogger } from '../../../../utils/logger'

const logger = namedLogger(LoggerName.useErtRecordingData)

export type ErtRecordingData = {
  webRecordings: ValidatedRecording[] | undefined
  audioRecordings: ValidatedRecording[] | undefined
  processedEvents: ProcessedRRWebEvents | undefined
  processedActivities: ProcessedActivity[] | undefined
  // The recording pipeline's own loading state (segment fetches + assembly + processing). Callers combine
  // this with their own resource queries (engagement, alarm, activities) for the full page loading state.
  isRecordingsLoading: boolean
  error: string | undefined
}

type UseErtRecordingDataParams = {
  // The id whose recordings to load: an engagementId (QA) or an alarmId (alarm view).
  id: string | undefined
  locationId: string | undefined
  // Whether to include live-stream video captures in processing (false for alarms).
  includeVideo: boolean
  // Activities folded into the processed timeline; pass an empty array for alarms.
  activities: Activity[] | undefined
  // Set when the activities request failed, so processing proceeds without them instead of waiting forever.
  activitiesFailed?: boolean
}

// Shared ERT recording pipeline: fetches the web/audio/live-stream segments, assembles + validates them,
// runs the rrweb processing, and folds in activities. Extracted from ErtPage so the QA (engagement) and
// alarm views can drive the same pipeline with different inputs.
export const useErtRecordingData = ({ id, locationId, includeVideo, activities, activitiesFailed = false }: UseErtRecordingDataParams): ErtRecordingData => {
  const [error, setError] = useState<string>()
  const [processedEvents, setProcessedEvents] = useState<ProcessedRRWebEvents>()
  const [processedActivities, setProcessedActivities] = useState<ProcessedActivity[]>()

  const webRecordingSegmentsQuery = useRecordingSegments(id, RecordingType.web)
  const webRecordingSegments = webRecordingSegmentsQuery.data
  const audioRecordingSegmentsQuery = useRecordingSegments(id, RecordingType.audio)
  const audioRecordingSegments = audioRecordingSegmentsQuery.data
  const liveStreamRecordingSegmentsQuery = useRecordingSegments(id, RecordingType.video)
  const liveStreamRecordingSegments = liveStreamRecordingSegmentsQuery.data

  const getRecordingUrl = useRecordingUrlFn()
  const getMediatorAccessToken = useGetMediatorAccessTokenFn()
  const getStreamingUrl = useGetStreamingUrlFn()

  const mediatorFns: MediatorFns = useMemo(
    () => ({ getRecordingUrl, getMediatorAccessToken, getStreamingUrl }),
    [getRecordingUrl, getMediatorAccessToken, getStreamingUrl]
  )

  // Allocate blob urls for the live-stream video segments.
  const liveStreamCaptureUrlsById = useMemo(() => {
    if (liveStreamRecordingSegmentsQuery.isFetched) {
      if (liveStreamRecordingSegments) {
        return createObjectUrlsForLiveStreamCaptures(liveStreamRecordingSegments)
      } else {
        return new Map()
      }
    }
  }, [liveStreamRecordingSegmentsQuery.isFetched, liveStreamRecordingSegments])

  // Revoke the blob urls created above when they change / on unmount.
  useEffect(() => {
    if (liveStreamCaptureUrlsById) {
      return () => {
        for (const liveStreamCapture of liveStreamCaptureUrlsById.values()) {
          URL.revokeObjectURL(liveStreamCapture.blobUrl)
        }
      }
    }
  }, [liveStreamCaptureUrlsById])

  const [webRecordings, setWebRecordings] = useState<ValidatedRecording[]>()
  useEffect(() => {
    const doEffect = async () => {
      if (webRecordingSegmentsQuery.isFetched) {
        try {
          if (webRecordingSegments) {
            const recordings = await assembleAndValidateRecordingsFromSegments(webRecordingSegments)
            setWebRecordings(recordings)
          } else {
            setWebRecordings([])
          }
        } catch (e) {
          logger.error(e)
          setError('An error occurred while assembling the web recording segments.')
        }
      }
    }
    doEffect()
  }, [webRecordingSegmentsQuery.isFetched, webRecordingSegments])

  const [isProcessing, setIsProcessing] = useState(true)
  useEffect(() => {
    const doEffect = async () => {
      if (id && locationId && mediatorFns && webRecordings && liveStreamCaptureUrlsById && (activities || activitiesFailed) && !processedEvents) {
        setIsProcessing(true)
        try {
          const processed = await processRRWebEventsForErt(mediatorFns, webRecordings, locationId, id, liveStreamCaptureUrlsById, includeVideo)
          setProcessedEvents(processed)
          setProcessedActivities(processActivities(processed, activities))
        } catch (e) {
          logger.error(e)
          setError('An error occurred while processing the recording.')
        }
        setIsProcessing(false)
      }
    }
    doEffect()
  }, [id, liveStreamCaptureUrlsById, locationId, mediatorFns, webRecordings, includeVideo, activities, activitiesFailed, processedEvents])

  const [audioRecordings, setAudioRecordings] = useState<ValidatedRecording[]>()
  useEffect(() => {
    const doEffect = async () => {
      if (audioRecordingSegmentsQuery.isFetched) {
        if (audioRecordingSegments && processedEvents) {
          try {
            const recordings = await assembleAndValidateRecordingsFromSegments(audioRecordingSegments, processedEvents.timeOfFirstEvent)
            setAudioRecordings(recordings)
          } catch (e) {
            logger.error(e)
            setError('An error occurred while assembling the audio recording segments.')
          }
        } else {
          setAudioRecordings([])
        }
      }
    }
    doEffect()
  }, [audioRecordingSegmentsQuery.isFetched, audioRecordingSegments, processedEvents])

  const isRecordingsLoading =
    webRecordingSegmentsQuery.isLoading ||
    audioRecordingSegmentsQuery.isLoading ||
    liveStreamRecordingSegmentsQuery.isLoading ||
    !webRecordings ||
    !audioRecordings ||
    isProcessing

  return { webRecordings, audioRecordings, processedEvents, processedActivities, isRecordingsLoading, error }
}
